import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { OrganizationQuery } from '@core/organization/organization.query';
import { SubscriptionAccessService } from '@core/subscription/subscription-access.service';
import { fundDDQRunsRoute, selectCompanyAbsoluteRoute } from '@shared/shared-routes';
import { Observable, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

import { AuthorizationService } from './authorization.service';
import { AppStoreQuery } from '@core/state';
import { ClientQuestionnaireCheckService } from '@feature/clients/client-dashboard/baskets/advisory-document/client-questionnaire-check/client-questionnaire-check.service';

@Injectable({
  providedIn: 'root',
})
export class IsInvestorGuard {
  constructor(
    private router: Router,
    private organizationQuery: OrganizationQuery,
    private authorization: AuthorizationService,
    private appStoreQuery: AppStoreQuery,
    private subscriptionAccessService: SubscriptionAccessService,
    private clientQuestionerCheckService: ClientQuestionnaireCheckService,
  ) {}

  public canActivate(): Observable<boolean | UrlTree> {
    return this.appStoreQuery.selectUserProfile().pipe(
      switchMap((data) => {
        const isClient = data?.isClient;
        if (isClient) {
          return this.clientQuestionerCheckService.getIncompleteFields().pipe(
            switchMap((incompleteFields) => {
              if (incompleteFields && incompleteFields.length > 0) {
                return of(this.router.parseUrl(`/clients/${data.client.uid}/general-investor`));
              }
              // DD-only partners have no Marketplace nav — land on Fund DDQ Runs instead.
              return this.subscriptionAccessService.selectIsDueDiligenceOnlyPartner().pipe(
                map((isDdOnly) =>
                  this.router.parseUrl(
                    isDdOnly ? `/${fundDDQRunsRoute}` : `/client/${data.client.uid}/marketplace`,
                  ),
                ),
              );
            }),
            first(),
          );
        } else {
          return this.organizationQuery.selectChosenOrganizationType().pipe(
            map(
              (type) => this.authorization.isInvestorCompany(type) || this.router.parseUrl(selectCompanyAbsoluteRoute),
            ),
            first(),
          );
        }
      }),
    );
  }

  public canLoad(): Observable<boolean> | Promise<boolean> | boolean {
    return this.organizationQuery.selectChosenOrganizationType().pipe(
      filter((type) => type !== null),
      map((type) => {
        return this.authorization.isInvestorCompany(type);
      }),
      first(),
    );
  }
}
